import { computed, ref } from 'vue'
import * as api from '@/modules/billing-templates/api'
export * from './types'

const billingTemplatesList = ref<api.GetBillingTemplatesListResponse>([])

export const deliveryPolicyTypeList = [
    {
        label: 'Email',
        value: 'email'
    },
    {
        label: 'Electronic invoice',
        value: 'electronic'
    },
    {
        label: 'Mail',
        value: 'mail'
    }]

export const countryCodesList = [
    {
        label: 'Finland',
        value: 'FI'
    },
    {
        label: 'Norway',
        value: 'NO'
    },
    {
        label: 'Sweden',
        value: 'SE'
    }
]

export const addressLabels = {
    street: 'Street',
    city: 'City',
    zip: 'Zip',
    countryCode: 'Country'
}

export const contactDataLabels = {
    contactPersonEmail: 'Contact email',
    contactPersonName: 'Contact name'
}

export const electronicLabels = {
    electronicAddress: 'Billing Electronic Address',
    electronicOperator: 'Billing Electronic Operator'
}

const getBillingTemplatesList = (organizationId: string) => api.getBillingTemplatesList(organizationId).then(billingTemlatesEnumeration => {
    billingTemplatesList.value = billingTemlatesEnumeration
})

const withBillingTemplateListUpdate = async (organizationId: string, callback: () => void) => {
    await callback()
    await getBillingTemplatesList(organizationId)
}

const addMailBillingTemplate = (organizationId: string, payload: api.AddMailBillingTemplateRequest) => withBillingTemplateListUpdate(organizationId, () => api.addMailBillingTemplate(organizationId, payload))

const addEmailBillingTemplate = (organizationId: string, payload: api.AddEmailBillingTemplateRequest) => withBillingTemplateListUpdate(organizationId, () => api.addEmailBillingTemplate(organizationId, payload))

const addElectronicBillingTemplate = (organizationId: string, payload: api.AddElectronicBillingTemplateRequest) => withBillingTemplateListUpdate(organizationId, () => api.addElectronicBillingTemplate(organizationId, payload))

const markBillingTemplateAsDefault = (organizationId: string, billingTemplateId: string, payload: api.MarkDefaultBillingTemplateRequest) => withBillingTemplateListUpdate(organizationId, () => api.markBillingTemplateAsDefault(organizationId, billingTemplateId, payload))

const deleteBillingTemplate = (organizationId: string, billingTemplateId: string) => withBillingTemplateListUpdate(organizationId, () => api.deleteBillingTemplate(organizationId, billingTemplateId))

const getBillingTemplate = async (organizationId: string, billingTemplateId: string) => {
    const billingTemplate = await api.getBillingTemplate(organizationId, billingTemplateId)
    if (billingTemplatesList.value.length) {
        const billingTemplateIndex = billingTemplatesList.value.findIndex(value => value.billingTemplateId === billingTemplateId)
        billingTemplatesList.value[billingTemplateIndex] = billingTemplate
    }
    return billingTemplate
}

const withBillingTemplateUpdate = async (organizationId: string, billingTemplateId: string, callback: () => void) => {
    await callback()
    await getBillingTemplate(organizationId, billingTemplateId)
}

const updateBillingTemplateName = (organizationId: string, billingTemplateId: string, payload: api.UpdateBillingTemplateNameRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updateBillingTemplateName(organizationId, billingTemplateId, payload))

const updateOrganizationName = (organizationId: string, billingTemplateId: string, payload: api.UpdateOrganizationNameRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updateOrganizationName(organizationId, billingTemplateId, payload))

const updateBusinessId = (organizationId: string, billingTemplateId: string, payload: api.UpdateBusinessIdRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updateBusinessId(organizationId, billingTemplateId, payload))
const updateVatId = (organizationId: string, billingTemplateId: string, payload: api.UpdateVatIdRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updateVatId(organizationId, billingTemplateId, payload))

const updateCustomerNumber = (organizationId: string, billingTemplateId: string, payload: api.UpdateCustomerNumberRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updateCustomerNumber(organizationId, billingTemplateId, payload))

const updatePaymentTerms = (organizationId: string, billingTemplateId: string, payload: api.UpdatePaymentTermsRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updatePaymentTerms(organizationId, billingTemplateId, payload))

const updateReference = (organizationId: string, billingTemplateId: string, payload: api.UpdateReferenceRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updateReference(organizationId, billingTemplateId, payload))

const updateContactData = (organizationId: string, billingTemplateId: string, payload: api.UpdateContactDataRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updateContactData(organizationId, billingTemplateId, payload))

const updateAddress = (organizationId: string, billingTemplateId: string, payload: api.UpdateAddressRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updateAddress(organizationId, billingTemplateId, payload))

const updateInvoiceDescription = (organizationId: string, billingTemplateId: string, payload: api.UpdateInvoiceDescriptionRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.updateInvoiceDescription(organizationId, billingTemplateId, payload))

const assignElectronicDeliveryPolicy = (organizationId: string, billingTemplateId: string, payload: api.AssignElectronicDeliveryPolicyRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.assignElectronicDeliveryPolicy(organizationId, billingTemplateId, payload))

const assignEmailDeliveryPolicy = (organizationId: string, billingTemplateId: string, payload: api.AssignEmailDeliveryPolicyRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.assignEmailDeliveryPolicy(organizationId, billingTemplateId, payload))

const assignMailDeliveryPolicy = (organizationId: string, billingTemplateId: string, payload: api.AssignMailDeliveryPolicyRequest) =>
    withBillingTemplateUpdate(organizationId, billingTemplateId, () => api.assignMailDeliveryPolicy(organizationId, billingTemplateId, payload))

export function useBillingTemplateModule () {
    return {
        billingTemplatesList: computed(() => billingTemplatesList.value),
        getBillingTemplatesList,
        getBillingTemplate,
        addMailBillingTemplate,
        addEmailBillingTemplate,
        addElectronicBillingTemplate,
        updateBillingTemplateName,
        updateOrganizationName,
        updateBusinessId,
        updateVatId,
        updateCustomerNumber,
        updatePaymentTerms,
        updateReference,
        updateContactData,
        updateAddress,
        updateInvoiceDescription,
        assignElectronicDeliveryPolicy,
        assignEmailDeliveryPolicy,
        assignMailDeliveryPolicy,
        deleteBillingTemplate,
        markBillingTemplateAsDefault,
        getBillingTemplateWithoutUpdate: (...args: Parameters<typeof api.getBillingTemplate>) => api.getBillingTemplate(...args),
        getBillingTemplatesFromApi: api.getBillingTemplatesList
    }
}
